import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { api } from "../services";
import { Button } from "./ui";
import {
  clearPartnerSession,
  readPartnerSession,
  writePartnerSession,
} from "../utils/partnerSession";

async function finishPartnerSession(status, extra = {}) {
  const session = readPartnerSession();
  if (!session?.id) return;
  const token = localStorage.getItem("token");
  const links = Array.isArray(session.items)
    ? session.items.map((item, idx) => {
      const key = `${item?.channelKey || item?.appId || "app"}:${item?.branchId || item?.branchRef || idx}`;
      const st = session.itemStatus?.[key] || (session.linkStatus === "live" ? "live" : "pending");
      return {
        branch_id: item.branchId || session.branchId || null,
        channel: item.channelKey || item.channelName || session.channelName || null,
        link_status: st === "live" ? "live" : "pending",
      };
    })
    : undefined;
  const res = await api.post(
    `/partner-sessions/${session.id}/complete`,
    {
      status,
      link_status: status === "completed" ? "live" : status === "pending" ? "pending" : undefined,
      branch_id: session.branchId || undefined,
      channel: session.channelName || undefined,
      links,
      ...extra,
    },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  clearPartnerSession();
  const redirectUrl = res.data?.redirect_url || session.returnUrl;
  if (redirectUrl) window.location.assign(redirectUrl);
}

export async function cancelPartnerSession() {
  return finishPartnerSession("cancelled");
}

export async function returnToPartner() {
  const session = readPartnerSession();
  const statuses = Object.values(session?.itemStatus || {});
  const allLive = statuses.length > 0 && statuses.every((s) => s === "live");
  const status = allLive || session?.linkStatus === "live" ? "completed" : "pending";
  return finishPartnerSession(status);
}

export function markPartnerBranchLive(branchId) {
  const session = readPartnerSession();
  if (!session) return;
  if (session.branchId && branchId && String(session.branchId) !== String(branchId)) return;
  const connectingKey = session.connectingKey;
  const itemStatus = { ...(session.itemStatus || {}) };
  if (connectingKey) itemStatus[connectingKey] = "live";
  writePartnerSession({
    ...session,
    linkStatus: "live",
    branchId: branchId || session.branchId || null,
    itemStatus,
  });
}

export default function PartnerSessionBanner() {
  const { t } = useTranslation("translation");
  const location = useLocation();
  const [session, setSession] = useState(() => readPartnerSession());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sync = () => setSession(readPartnerSession());
    window.addEventListener("partner-session", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("partner-session", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // Focused partner shell has its own top bar.
  if (location.pathname.startsWith("/partner-sessions/")) return null;
  if (!session?.id || !session.partnerName) return null;

  const goBack = async () => {
    setBusy(true);
    try {
      await returnToPartner();
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
      setBusy(false);
    }
  };

  return (
    <div
      className="partner-session-banner"
      style={{
        display: "flex",
        justifyContent: "space-between",
        gap: 12,
        alignItems: "center",
        padding: "10px 16px",
        background: "#0b2d3c",
        color: "#fff",
      }}
    >
      <span>{session.partnerName}</span>
      <Button color="light" size="sm" disabled={busy} onClick={goBack}>
        {t("partnerSessionBack", { partner: session.partnerName })}
      </Button>
    </div>
  );
}
